import React, { Component } from 'react'
import { NotificationManager } from 'react-notifications'
import { Buffer } from 'buffer'
import { FaImage, FaFilm, FaSave, FaArrowLeft, FaTrash } from "react-icons/fa"

import CardService from "../../../services/card_services"

const CURRENCIES = [
    { value: 0, label: 'Coins' },
    { value: 1, label: 'Gems' },
    { value: 2, label: 'IRL Reward' },
]

const IMG_EXT = ['png', 'jpg', 'jpeg', 'webp']
const VIDEO_EXT = ['mp4', 'webm', 'mov']

// Convert a full URL like ".../sandbox/app/pack/<uuid>.jpg" to filename "<uuid>.jpg"
const fileBasename = (url) => {
    if (!url) return null
    try {
        const parts = url.split('/')
        return parts[parts.length - 1]
    } catch (_) {
        return url
    }
}

class PackForm extends Component {
    constructor(props) {
        super(props)
        this.state = {
            processing: false,
            success: false,

            // form fields
            pack_name: '',
            nb_cards: 5,
            price: 0,
            currency: 0,
            rcat_product_id: '',
            initial_rcat_product_id: '',
            collections: [],            // array of collection uuids
            start_date: '',
            end_date: '',
            description_en: '',
            description_fr: '',

            // visual data (newly uploaded)
            cover_data: null,
            cover_extension: null,
            animation_data: null,
            animation_extension: null,

            // re-use existing files
            cover_name: null,
            animation_name: null,
            cover_url: null,            // for preview if cover_name is set
            animation_url: null,

            availableCollections: [],
            availableFiles: [],         // pack files urls
            availableCurrencies: CURRENCIES,
        }
    }

    async componentDidMount() {
        try {
            const collections = await CardService.getCollections()
            this.setState({ availableCollections: collections })
        } catch (e) {
            // ignore
        }
        try {
            // GET /card/pack returns the currency enum as { id: label }
            const data = await CardService.getPacks(true)
            if (data && data.currency && typeof data.currency === 'object') {
                const currencies = Object.entries(data.currency).map(([value, label]) => ({ value: parseInt(value, 10), label }))
                if (currencies.length) this.setState({ availableCurrencies: currencies })
            }
        } catch (e) {
            // ignore, fallback to default currencies
        }
        try {
            const files = await CardService.getPackFiles()
            this.setState({ availableFiles: files || [] })
        } catch (e) {
            // ignore
        }
        if (this.props.initialPack) {
            this.applyInitial(this.props.initialPack)
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.initialPack && this.props.initialPack !== prevProps.initialPack) {
            this.applyInitial(this.props.initialPack)
        }
    }

    applyInitial = (pack) => {
        this.setState({
            pack_name: pack.pack_name || '',
            nb_cards: pack.nb_cards || 5,
            price: pack.price || 0,
            currency: typeof pack.currency === 'number' ? pack.currency : 0,
            rcat_product_id: pack.rcat_product_id || '',
            initial_rcat_product_id: pack.rcat_product_id || '',
            collections: pack.collections || [],
            start_date: pack.start_date ? this.toLocalDateTime(pack.start_date) : '',
            end_date: pack.end_date ? this.toLocalDateTime(pack.end_date) : '',
            description_en: pack.description && pack.description.en ? pack.description.en : '',
            description_fr: pack.description && pack.description.fr ? pack.description.fr : '',
            cover_url: pack.cover || null,
            cover_name: pack.cover ? fileBasename(pack.cover) : null,
            animation_url: pack.animation || null,
            animation_name: pack.animation ? fileBasename(pack.animation) : null,
        })
    }

    toLocalDateTime = (iso) => {
        try {
            const d = new Date(iso)
            const pad = (n) => String(n).padStart(2, '0')
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
        } catch (_) {
            return ''
        }
    }

    onCoverHandler = async (event) => {
        const file = event.target.files[0]
        if (!file) return
        const extension = file.name.split('.').pop().toLowerCase()
        if (!IMG_EXT.includes(extension)) {
            NotificationManager.error('Invalid image (png/jpg/jpeg/webp)')
            return
        }
        if (file.size > 5000000) {
            NotificationManager.error('Cover exceeding 5MB')
            return
        }
        const reader = new FileReader()
        reader.onloadend = () => {
            const base64data = Buffer.from(reader.result).toString('base64')
            this.setState({
                cover_data: `data:image/${extension};base64,${base64data}`,
                cover_extension: extension,
                cover_name: null,
                cover_url: null,
            })
        }
        reader.readAsArrayBuffer(file)
    }

    onAnimationHandler = async (event) => {
        const file = event.target.files[0]
        if (!file) return
        const extension = file.name.split('.').pop().toLowerCase()
        if (!VIDEO_EXT.includes(extension)) {
            NotificationManager.error('Invalid video (mp4/webm/mov)')
            return
        }
        if (file.size > 25000000) {
            NotificationManager.error('Animation exceeding 25MB')
            return
        }
        const reader = new FileReader()
        reader.onloadend = () => {
            const base64data = Buffer.from(reader.result).toString('base64')
            this.setState({
                animation_data: `data:video/${extension};base64,${base64data}`,
                animation_extension: extension,
                animation_name: null,
                animation_url: null,
            })
        }
        reader.readAsArrayBuffer(file)
    }

    pickExistingCover = (url) => {
        this.setState({
            cover_url: url,
            cover_name: fileBasename(url),
            cover_data: null,
            cover_extension: null,
        })
    }

    pickExistingAnimation = (url) => {
        this.setState({
            animation_url: url,
            animation_name: fileBasename(url),
            animation_data: null,
            animation_extension: null,
        })
    }

    clearCover = () => {
        this.setState({ cover_data: null, cover_extension: null, cover_name: null, cover_url: null })
    }

    clearAnimation = () => {
        this.setState({ animation_data: null, animation_extension: null, animation_name: null, animation_url: null })
    }

    toggleCollection = (uuid) => {
        const list = [...this.state.collections]
        const idx = list.indexOf(uuid)
        if (idx >= 0) list.splice(idx, 1)
        else list.push(uuid)
        this.setState({ collections: list })
    }

    buildPayload = () => {
        const {
            pack_name, nb_cards, price, currency, collections,
            start_date, end_date, description_en, description_fr,
            cover_data, cover_name, animation_data, animation_name,
            rcat_product_id, initial_rcat_product_id,
        } = this.state

        const payload = {
            pack_name,
            nb_cards: parseInt(nb_cards, 10),
            price: parseInt(price, 10),
            currency: parseInt(currency, 10),
            collections,
        }

        const rcatId = rcat_product_id.trim()
        if (rcatId) {
            payload.rcat_product_id = rcatId
        } else if (this.props.mode === 'update' && initial_rcat_product_id) {
            payload.rcat_product_id = null
        }

        if (start_date) payload.start_date = new Date(start_date).toISOString()
        if (end_date) payload.end_date = new Date(end_date).toISOString()

        if (description_en || description_fr) {
            payload.description = { en: description_en || '', fr: description_fr || '' }
        }

        if (cover_data) {
            payload.cover_data = cover_data
        } else if (cover_name && this.props.mode === 'update') {
            payload.cover_name = cover_name
        }

        if (animation_data) {
            payload.animation_data = animation_data
        } else if (animation_name && this.props.mode === 'update') {
            payload.animation_name = animation_name
        }

        return payload
    }

    submit = async () => {
        const { pack_name, nb_cards, price, collections, cover_data, cover_name } = this.state
        if (!pack_name || !nb_cards || price === '' || price === null || !collections.length) {
            NotificationManager.error('Missing required fields (name, nb_cards, price, collections)')
            return
        }
        if (this.props.mode === 'create' && !cover_data && !cover_name) {
            NotificationManager.error('A cover image is required')
            return
        }

        const payload = this.buildPayload()
        try {
            this.setState({ processing: true })
            const data = await this.props.onSubmit(payload)
            NotificationManager.success(data.message || 'Saved')
            this.setState({ processing: false, success: true })
            if (this.props.mode === 'create') {
                setTimeout(() => { window.location.href = '/admin/packs' }, 800)
            }
        } catch (e) {
            this.setState({ processing: false })
            NotificationManager.error(e.response && e.response.data ? e.response.data.message : e.message)
        }
    }

    render() {
        const s = this.state
        const isVideo = (url) => url && /\.(mp4|webm|mov)(\?|$)/i.test(url)

        return (
            <div className="max-w-4xl mx-auto space-y-6">
                {/* Visual Assets */}
                <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 space-y-6">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <FaImage className="text-fuchsia-500" />
                        Cover Image *
                    </h3>

                    {(s.cover_data || s.cover_url) && (
                        <div className="flex justify-center">
                            <img
                                src={s.cover_data || s.cover_url}
                                alt="Pack cover"
                                className="max-w-xs rounded-lg shadow-md border-2 border-gray-300"
                            />
                        </div>
                    )}

                    <input
                        type="file"
                        onChange={this.onCoverHandler}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-fuchsia-50 file:text-fuchsia-700 hover:file:bg-fuchsia-100 cursor-pointer"
                    />
                    <p className="text-xs text-gray-500">PNG / JPG / WEBP, max 5MB</p>

                    {(s.cover_data || s.cover_url) && (
                        <button
                            onClick={this.clearCover}
                            className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-lg hover:bg-red-200 inline-flex items-center gap-1"
                        >
                            <FaTrash /> Clear cover
                        </button>
                    )}

                    {s.availableFiles && s.availableFiles.length > 0 && (
                        <div className="pt-4 border-t border-gray-100">
                            <p className="text-sm font-semibold text-gray-700 mb-2">Or pick from existing files:</p>
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-48 overflow-y-auto">
                                {s.availableFiles.filter(u => !isVideo(u)).map(url => (
                                    <button
                                        type="button"
                                        key={url}
                                        onClick={() => this.pickExistingCover(url)}
                                        className={`relative border-2 rounded-lg overflow-hidden ${s.cover_url === url ? 'border-fuchsia-500' : 'border-gray-200 hover:border-gray-400'}`}
                                        title={fileBasename(url)}
                                    >
                                        <div
                                            className="w-full h-16 bg-cover bg-center"
                                            style={{ backgroundImage: `url('${url}')` }}
                                        />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Animation (optional) */}
                <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 space-y-6">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <FaFilm className="text-purple-500" />
                        Animation (optional)
                    </h3>

                    {(s.animation_data || s.animation_url) && (
                        <div className="flex justify-center">
                            <video
                                src={s.animation_data || s.animation_url}
                                controls
                                className="max-w-xs rounded-lg shadow-md border-2 border-gray-300"
                            />
                        </div>
                    )}

                    <input
                        type="file"
                        onChange={this.onAnimationHandler}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 cursor-pointer"
                    />
                    <p className="text-xs text-gray-500">MP4 / WEBM / MOV, max 25MB</p>

                    {(s.animation_data || s.animation_url) && (
                        <button
                            onClick={this.clearAnimation}
                            className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-lg hover:bg-red-200 inline-flex items-center gap-1"
                        >
                            <FaTrash /> Clear animation
                        </button>
                    )}

                    {s.availableFiles && s.availableFiles.filter(u => isVideo(u)).length > 0 && (
                        <div className="pt-4 border-t border-gray-100">
                            <p className="text-sm font-semibold text-gray-700 mb-2">Or pick from existing animations:</p>
                            <div className="flex flex-wrap gap-2">
                                {s.availableFiles.filter(u => isVideo(u)).map(url => (
                                    <button
                                        type="button"
                                        key={url}
                                        onClick={() => this.pickExistingAnimation(url)}
                                        className={`px-3 py-1 text-xs border rounded-lg ${s.animation_url === url ? 'bg-purple-100 border-purple-500 text-purple-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                                    >
                                        {fileBasename(url)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Pack Details */}
                <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 space-y-4">
                    <h3 className="text-lg font-bold text-gray-900">Pack Details</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Pack Name *</label>
                            <input
                                type="text"
                                value={s.pack_name}
                                onChange={(e) => this.setState({ pack_name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Number of Cards *</label>
                            <input
                                type="number"
                                min="1"
                                value={s.nb_cards}
                                onChange={(e) => this.setState({ nb_cards: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Price *</label>
                            <input
                                type="number"
                                min="0"
                                value={s.price}
                                onChange={(e) => this.setState({ price: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Currency *</label>
                            <select
                                value={s.currency}
                                onChange={(e) => this.setState({ currency: parseInt(e.target.value, 10) })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent outline-none"
                            >
                                {s.availableCurrencies.map(c => (
                                    <option key={c.value} value={c.value}>{c.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">RevenueCat Product ID</label>
                            <input
                                type="text"
                                value={s.rcat_product_id}
                                onChange={(e) => this.setState({ rcat_product_id: e.target.value })}
                                placeholder="prodxxxxxxxx"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent outline-none"
                            />
                            <p className="text-xs text-gray-500 mt-1">Optional, only for payment in EUR</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                            <input
                                type="datetime-local"
                                value={s.start_date}
                                onChange={(e) => this.setState({ start_date: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                            <input
                                type="datetime-local"
                                value={s.end_date}
                                onChange={(e) => this.setState({ end_date: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent outline-none"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description (English)</label>
                        <textarea
                            value={s.description_en}
                            onChange={(e) => this.setState({ description_en: e.target.value })}
                            rows="3"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent outline-none resize-none"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description (French)</label>
                        <textarea
                            value={s.description_fr}
                            onChange={(e) => this.setState({ description_fr: e.target.value })}
                            rows="3"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent outline-none resize-none"
                        />
                    </div>
                </div>

                {/* Collections */}
                <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 space-y-4">
                    <h3 className="text-lg font-bold text-gray-900">Collections * <span className="text-sm font-normal text-gray-500">(at least one)</span></h3>
                    {s.availableCollections.length === 0 ? (
                        <p className="text-sm text-gray-500">No collections available.</p>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {s.availableCollections.map(c => {
                                const checked = s.collections.includes(c.collection_uuid)
                                return (
                                    <label key={c.collection_uuid} className={`flex items-center gap-2 p-2 border rounded-lg cursor-pointer transition-colors ${checked ? 'border-fuchsia-500 bg-fuchsia-50' : 'border-gray-300 hover:bg-gray-50'}`}>
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => this.toggleCollection(c.collection_uuid)}
                                            className="w-4 h-4 text-fuchsia-600 focus:ring-fuchsia-500"
                                        />
                                        <span className="text-sm font-medium text-gray-800">{c.collection_name}</span>
                                    </label>
                                )
                            })}
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                    <button
                        onClick={() => window.location.href = '/admin/packs'}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium flex items-center gap-2"
                    >
                        <FaArrowLeft /> Back
                    </button>
                    <button
                        onClick={this.submit}
                        disabled={s.processing}
                        className="px-6 py-2 bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white rounded-lg hover:from-fuchsia-600 hover:to-purple-700 transition-all font-medium shadow-md hover:shadow-lg flex items-center gap-2 disabled:opacity-50"
                    >
                        <FaSave /> {s.processing ? 'Saving...' : (this.props.submitLabel || 'Save')}
                    </button>
                </div>
            </div>
        )
    }
}

export default PackForm
